import { assertNever } from '../../helpers';
import { WebsocketProjectData } from '../../websocket/server-message-event.model';
import { NewsfeedApiResponseEntry } from './api.model';
import {
  NotificationEntry,
  NotificationProjectData,
} from './notifications.model';

export function stringToNumber(x?: number | string): number | undefined {
  return x ? Number(x) : undefined;
}

export function isTest(entry: NewsfeedApiResponseEntry) {
  switch (entry.type) {
    case 'xpContest':
    case 'contest':
    case 'project':
    case 'quickHireProject':
    case 'inviteUserBid':
    case 'notifyfollower':
    case 'failingProject':
    case 'localJobPosted':
      // case 'localJobsMobile':
      return entry.data.isTest;
    default:
      return false;
  }
}

export function transformNavigationNotification(
  entry: NewsfeedApiResponseEntry,
  userId: string,
): NotificationEntry | undefined {
  if (isTest(entry)) {
    return undefined;
  }

  switch (entry.type) {
    // There are:
    // - Types that shouldn't be in notification feed
    case 'contest':
    case 'failingProject':
    case 'project':
    case 'xpContest':
    case 'localJobPosted':
      return undefined;
    // Types that are not implemented
    case 'contestExpired':
    case 'serviceStatusChanged':
      return undefined;

    case 'articleCommentReceived':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: Number(entry.data.id),
        data: {
          articleAuthor: Number(entry.data.article_author), // The ID of author
          articleName: entry.data.article_name,
          articleType: entry.data.article_type, // Some enum?
          commentBy: Number(entry.data.comment_by),
          commentDate: entry.data.comment_date,
          commentId: Number(entry.data.comment_id),
          comment: entry.data.comment,
          imgUrl: entry.data.imgUrl,
          linkUrl: entry.data.linkUrl,
          publicName: entry.data.publicName,
          time: Number(entry.data.time) * 1000,
          user: entry.data.user,
        },
      };
    case 'award':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.data.id,
        data: {
          imgUrl: entry.data.imgUrl,
          linkUrl: entry.data.linkUrl,
          time: entry.data.time * 1000,
          title: entry.data.title,
          username: entry.data.userName,
        },
      };
    case 'awardBadge':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.data.id,
        data: {
          ...entry.data,
          imgUrl: undefined,
          time: entry.data.time * 1000,
        },
      };
    case 'awardCredit':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: Number(entry.data.id),
        data: {
          amount: Number(entry.data.amount),
          imgUrl: undefined,
          linkUrl: entry.data.linkUrl,
          time: entry.data.time * 1000,
        },
      };
    case 'awardReminder':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: Number(entry.data.bid.id),
        data: {
          imgUrl: entry.data.imgUrl,
          linkUrl: `${entry.data.linkUrl}?gotoBid=${entry.data.bid.id}`,
          name: entry.data.name,
          time: entry.data.time * 1000,
          username: entry.data.userName,
        },
      };
    case 'awardMilestoneReminder':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.data.id,
        data: {
          appendedDescr: entry.data.appended_descr,
          currencyCode: entry.data.currencycode,
          currencyId: entry.data.currencyid,
          currencySign: entry.data.currencysign,
          imgUrl: entry.data.imgUrl,
          linkUrl: entry.data.linkUrl,
          time: entry.data.time * 1000,
          title: entry.data.title,
          userId: entry.data.userId,
          username: entry.data.userName,
        },
      };
    case 'awardXp':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: Number(entry.data.id),
        data: {
          amount: Number(entry.data.amount),
          descr: entry.data.descr,
          imgUrl: undefined,
          linkUrl: entry.data.linkUrl,
          time: entry.data.time * 1000,
        },
      };
    case 'bid':
    case 'bid_self':
    case 'bid_updated':
      return {
        type: 'bid',
        parent_type: entry.parent_type,
        id: entry.data.id,
        data: {
          ...entry.data,
          linkUrl: `${entry.data.linkUrl}?gotoBid=${entry.data.bid.id}`,
          time: entry.data.time * 1000,
        },
      };
    case 'completed': {
      const toUserId =
        entry.data.freelancerId === userId
          ? entry.data.employerId
          : entry.data.freelancerId;

      let imgUrl;
      let username;

      if (entry.data.employerId === userId) {
        imgUrl = entry.data.img_f.profile_logo_url;
        username = entry.data.freelancerPublicName
          ? entry.data.freelancerPublicName
          : entry.data.freelancerUsername;
      } else {
        imgUrl = entry.data.img_e.profile_logo_url;
        username = entry.data.employerPublicName
          ? entry.data.employerPublicName
          : entry.data.employerUsername;
      }

      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: Number(entry.data.id),
        data: {
          amount: Number(entry.data.amount),
          categoryName: entry.data.categoryName,
          completeStatus: entry.data.completeStatus,
          currencyCode: entry.data.currencycode,
          currencyId: Number(entry.data.currencyid),
          currencySign: entry.data.currencysign,
          employerId: Number(entry.data.employerId),
          employerPublicName: entry.data.employerPublicName,
          employerUsername: entry.data.employerUsername,
          expiredTimestamp: entry.data.expiredTimestamp,
          freelancerId: Number(entry.data.freelancerId),
          freelancerPublicName: entry.data.freelancerPublicName,
          freelancerUsername: entry.data.freelancerUsername,
          imgUrl,
          name: entry.data.name,
          // projIsHourly: entry.data.projIsHourly,
          selectionId: Number(entry.data.selectionId),
          seoUrl: entry.data.seoUrl,
          state: entry.data.state,
          submitDate: entry.data.submitDate,
          time: entry.data.time * 1000,
          username,
          linkUrl: `/users/review/rate.php?id=${entry.data
            .id}&to_user=${toUserId}`,
          imgE: {
            ...entry.data.img_e,
          },
          imgF: {
            ...entry.data.img_f,
          },
        },
      };
    }
    case 'contestAwardedToEmployer':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.data.id,
        data: {
          ...entry.data,
          entryId: entry.data.entry_id,
          entryNumber: entry.data.entry_number,
          time: entry.data.time * 1000,
          username: entry.data.userName,
        },
      };
    case 'contestAwardedToFreelancer':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.data.id,
        data: {
          ...entry.data,
          entryId: entry.data.entry_id,
          entryNumber: entry.data.entry_number,
          time: entry.data.time * 1000,
          username: entry.data.userName,
        },
      };
    case 'contestEntry':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.data.id,
        data: {
          ...entry.data,
          time: entry.data.time * 1000,
        },
      };
    case 'contestEntryBoughtToEmployer':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.data.id,
        data: {
          ...entry.data,
          entryId: entry.data.entry_id,
          entryNumber: entry.data.entry_number,
          time: entry.data.time * 1000,
          username: entry.data.userName,
        },
      };
    case 'contestEntryBoughtToFreelancer':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.data.id,
        data: {
          ...entry.data,
          entryId: entry.data.entry_id,
          entryNumber: entry.data.entry_number,
          time: entry.data.time * 1000,
          username: entry.data.userName,
        },
      };
    case 'contestEntryCommentedToFreelancer': {
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: Number(entry.data.id),
        data: {
          contestId: Number(entry.data.contestId),
          contestName: entry.data.contestName,
          entryNumber: Number(entry.data.entryNumber),
          imgUrl: entry.data.imgUrl,
          linkUrl: entry.data.linkUrl,
          time: Number(entry.data.time) * 1000,
        },
      };
    }
    case 'contestPCBNotification': {
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.data.id,
        data: {
          ...entry.data,
          time: entry.data.time * 1000,
        },
      };
    }
    case 'contestPCBNotificationFullView': {
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.data.id,
        data: {
          ...entry.data,
          time: entry.data.time * 1000,
        },
      };
    }
    case 'contestEntryRatedToFreelancer': {
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: Number(entry.data.id),
        data: {
          action: entry.data.action,
          contestId: Number(entry.data.contestId),
          contestName: entry.data.contestName,
          employerId: Number(entry.data.employerId),
          entriesUrl: entry.data.entriesUrl,
          entryId: Number(entry.data.entryId),
          entryNumber: Number(entry.data.entryNumber),
          entryS3path: entry.data.entryS3path,
          entryThumb: entry.data.entryThumb,
          entryUrl: entry.data.entryUrl,
          freelancerId: Number(entry.data.freelancerId),
          hasOtherEntriesRatedOrRejected:
            entry.data.hasOtherEntriesRatedOrRejected,
          imgUrl: entry.data.imgUrl,
          linkUrl: entry.data.linkUrl,
          ratedOrRejectedEntriesCount: Number(
            entry.data.ratedOrRejectedEntriesCount,
          ),
          rating: entry.data.rating,
          time: Number(entry.data.time) * 1000,
        },
      };
    }
    case 'contestEntryReconsideredToFreelancer': {
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: Number(entry.data.id),
        data: {
          action: entry.data.action,
          contestId: Number(entry.data.contestId),
          contestName: entry.data.contestName,
          employerId: Number(entry.data.employerId),
          entriesUrl: entry.data.entriesUrl,
          entryId: Number(entry.data.entryId),
          entryNumber: Number(entry.data.entryNumber),
          entryS3path: entry.data.entryS3path,
          entryThumb: entry.data.entryThumb,
          entryUrl: entry.data.entryUrl,
          freelancerId: Number(entry.data.freelancerId),
          hasOtherEntriesRatedOrRejected:
            entry.data.hasOtherEntriesRatedOrRejected,
          imgUrl: entry.data.imgUrl,
          linkUrl: entry.data.linkUrl,
          ratedOrRejectedEntriesCount: Number(
            entry.data.ratedOrRejectedEntriesCount,
          ),
          rating: entry.data.rating,
          time: Number(entry.data.time) * 1000,
        },
      };
    }
    case 'contestEntryRejectedToFreelancer': {
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: Number(entry.data.id),
        data: {
          action: entry.data.action,
          contestId: Number(entry.data.contestId),
          contestName: entry.data.contestName,
          employerId: Number(entry.data.employerId),
          entriesUrl: entry.data.entriesUrl,
          entryId: Number(entry.data.entryId),
          entryNumber: Number(entry.data.entryNumber),
          entryS3path: entry.data.entryS3path,
          entryThumb: entry.data.entryThumb,
          entryUrl: entry.data.entryUrl,
          freelancerId: Number(entry.data.freelancerId),
          hasOtherEntriesRatedOrRejected:
            entry.data.hasOtherEntriesRatedOrRejected,
          imgUrl: entry.data.imgUrl,
          linkUrl: entry.data.linkUrl,
          ratedOrRejectedEntriesCount: Number(
            entry.data.ratedOrRejectedEntriesCount,
          ),
          rating: entry.data.rating,
          time: Number(entry.data.time) * 1000,
        },
      };
    }
    case 'createMilestone':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.data.id,
        data: {
          ...transformProjectData(entry.data),
          accountId: entry.data.account_id,
          amount: entry.data.amount,
          appendedDescr: entry.data.appended_descr,
          bidId: entry.data.bidId,
          currencyCode: entry.data.currencycode,
          currencySign: entry.data.currencysign,
          projectId: entry.data.project_id,
          shortDescr: entry.data.shortDescr,
          transId: entry.data.transId,
          time: entry.data.time * 1000,
          username: entry.data.userName,
        },
      };
    case 'denyed':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.data.id,
        data: {
          ...entry.data,
          time: entry.data.time * 1000,
          username: entry.data.userName,
        },
      };
    case 'draftContest':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: Number(entry.data.contestId),
        data: {
          ...entry.data,
          time: entry.data.time * 1000,
          linkUrl: entry.data.contestUrl,
        },
      };
    case 'highLtvAnnualDiscount':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.data.id,
        data: {
          ...entry.data,
          time: entry.data.time * 1000,
          imgUrl: undefined,
        },
      };
    case 'inviteUserBid':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.data.id,
        data: {
          ...transformProjectData(entry.data),
          currencyCode: entry.data.currencycode,
          currencySign: entry.data.currencysign,
          inviteeId: entry.data.inviteeId,
          inviteeName: entry.data.inviteeName,
          maxBudget: entry.data.maxBudget,
          minBudget: entry.data.minBudget,
          publicName: entry.data.publicName,
          time: entry.data.time * 1000,
        },
      };
    case 'inviteToContest':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.data.id,
        data: {
          ...entry.data,
          contestRecommendedSkills: entry.data.contest_recommended_skills,
          currencyCode: entry.data.currencycode,
          currencySign: entry.data.currencysign,
          time: entry.data.time * 1000,
        },
      };
    case 'invoiceRequested':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: Number(entry.data.id),
        data: {
          accountId: Number(entry.data.accountId),
          amount: Number(entry.data.amount),
          bidId: Number(entry.data.bidId),
          currencyCode: entry.data.currencyCode,
          currencySign: entry.data.currencySign,
          dueDate: entry.data.dueDate,
          imgUrl: entry.data.imgUrl,
          invoiceId: Number(entry.data.invoiceId),
          linkUrl: `${entry.data.linkUrl}#invoices_${entry.data.bidId}`,
          name: entry.data.name,
          publicName: entry.data.publicName,
          sellerId: Number(entry.data.sellerId),
          seoUrl: entry.data.seoUrl,
          state: entry.data.state,
          time: Number(entry.data.time) * 1000,
          userId: Number(entry.data.userId),
          username: entry.data.userName,
        },
      };
    case 'levelup':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.data.id,
        data: {
          ...entry.data,
          imgUrl: undefined,
          time: entry.data.time * 1000,
        },
      };
    case 'notifyfollower':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.data.id,
        data: transformProjectData(entry.data),
      };

    case 'projectRecruiter':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: Number(entry.data.id),
        data: {
          message: entry.data.message,
          time: entry.data.time * 1000,
          linkUrl: `/projects/${entry.data.project.id}.html`,
          // TODO T37473 Use UiConfig for base assets url
          imgUrl: `/assets/webapp/assets/recruiter/recruiter-badge.png`,
          agent: {
            username: entry.data.agent.username,
            id: Number(entry.data.agent.users_id),
          },
          project: {
            id: Number(entry.data.project.id),
            name: entry.data.project.name,
          },
        },
      };
    case 'hireMe':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: Number(entry.data.id),
        data: {
          currency: entry.data.currency,
          imgUrl: entry.data.imgUrl,
          linkUrl: entry.data.linkUrl,
          period: entry.data.period,
          projIsHourly: entry.data.projIsHourly,
          publicName: entry.data.publicName,
          sum: Number(entry.data.sum),
          title: entry.data.title,
          time: entry.data.time * 1000,
          username: entry.data.userName,
        },
      };
    case 'quickHireProject':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.data.id,
        data: {
          ...transformProjectData(entry.data),
          currency: entry.data.currency,
          period: entry.data.period,
          projIsHourly: entry.data.projIsHourly,
          publicName: entry.data.publicName,
          sum: Number(entry.data.sum),
        },
      };
    case 'releaseMilestone':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.data.id,
        data: {
          ...entry.data,
          currencyCode: entry.data.currencycode,
          currencySign: entry.data.currencysign,
          time: entry.data.time * 1000,
          username: entry.data.userName,
        },
      };
    case 'requestEndProject':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.data.id,
        data: {
          ...entry.data,
          time: entry.data.time * 1000,
          username: entry.data.userName,
        },
      };
    case 'requestMilestone': {
      const linkUrl = !entry.data.linkUrl
        ? `/projects/${entry.data.seoUrl}.html`
        : `${entry.data.linkUrl}#invoices_${entry.data.bidId}`;

      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: Number(entry.data.id),
        data: {
          ...entry.data,
          accountId: Number(entry.data.accountId),
          amount: Number(entry.data.amount),
          currencyId: Number(entry.data.currencyId),
          linkUrl,
          requestId: Number(entry.data.requestId),
          sellerId: Number(entry.data.sellerId),
          time: entry.data.time * 1000,
          username: entry.data.userName,
        },
      };
    }
    case 'requestToRelease': {
      const linkUrl = !entry.data.linkUrl
        ? `/projects/${entry.data.seoUrl}.html`
        : `${entry.data.linkUrl}#invoices_${entry.data.bidId}`;

      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: Number(entry.data.id),
        data: {
          accountId: Number(entry.data.accountId),
          amount: Number(entry.data.amount),
          bidId: Number(entry.data.bidId),
          currencyCode: entry.data.currencycode,
          currencySign: entry.data.currencysign,
          descr: entry.data.descr,
          // img: ???,
          imgUrl: entry.data.imgUrl,
          linkUrl,
          name: entry.data.name,
          publicName: entry.data.publicName,
          seoUrl: entry.data.seoUrl,
          state: entry.data.state,
          time: Number(entry.data.time) * 1000,
          tranId: Number(entry.data.tranId),
          userId: Number(entry.data.userId),
          username: entry.data.userName,
        },
      };
    }
    case 'serviceProjectEndingSeller':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: Number(entry.data.id),
        data: {
          bidId: Number(entry.data.bid_id),
          buyerId: Number(entry.data.buyer_id),
          buyerName: entry.data.buyer_name,
          hoursRemaining: Number(entry.data.hours_remaining),
          imgUrl: undefined,
          linkUrl: entry.data.project_url,
          orderStartDate: entry.data.order_start_date,
          projectId: Number(entry.data.project_id),
          projectName: entry.data.project_name,
          projectUrl: entry.data.project_url,
          sellerId: Number(entry.data.seller_id),
          sellerName: entry.data.seller_name,
          serviceId: Number(entry.data.service_id),
          serviceName: entry.data.service_name,
          time: entry.data.time * 1000,
        },
      };
    case 'serviceProjectPostedSeller':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: Number(entry.data.id),
        data: {
          bidId: Number(entry.data.bid_id),
          buyerId: Number(entry.data.buyer_id),
          buyerImgUrl: entry.data.buyer_img_url,
          buyerName: entry.data.buyer_name,
          buyerPublicName: entry.data.buyer_public_name,
          currencyCode: entry.data.currency_code,
          currencySign: entry.data.currency_sign,
          imgUrl: entry.data.buyer_img_url,
          linkUrl: entry.data.project_url,
          orderEndDate: entry.data.order_end_date,
          orderStartDate: entry.data.order_start_date,
          projectCost: Number(entry.data.project_cost),
          // projectDuration: entry.data.project_duration,
          projectId: Number(entry.data.project_id),
          // projectMilestones: entry.data.project_milestones,
          projectName: entry.data.project_name,
          projectUrl: entry.data.project_url,
          sellerId: Number(entry.data.seller_id),
          sellerImgUrl: entry.data.seller_img_url,
          sellerName: entry.data.seller_name,
          sellerPublicName: entry.data.seller_public_name,
          serviceId: Number(entry.data.service_id),
          serviceName: entry.data.service_name,
          serviceUrl: entry.data.service_url,
          time: Number(entry.data.time) * 1000,
        },
      };
    case 'serviceApproved':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: Number(entry.data.id),
        data: {
          imgUrl: undefined,
          linkUrl: entry.data.service_url,
          serviceId: Number(entry.data.service_id),
          serviceName: entry.data.service_name,
          serviceUrl: entry.data.service_url,
          serviceDuration: entry.data.service_duration,
          serviceCost: Number(entry.data.service_cost),
          currencySign: entry.data.currency_sign,
          currencyCode: entry.data.currency_code,
          serviceImg: entry.data.service_img, // A URL
          time: entry.data.time * 1000,
        },
      };
    case 'serviceRejected':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: Number(entry.data.id),
        data: {
          imgUrl: undefined,
          linkUrl: entry.data.service_url,
          serviceId: Number(entry.data.service_id),
          serviceName: entry.data.service_name,
          serviceUrl: entry.data.service_url,
          serviceDuration: entry.data.service_duration,
          serviceCost: Number(entry.data.service_cost),
          currencySign: entry.data.currency_sign,
          currencyCode: entry.data.currency_code,
          serviceImg: entry.data.service_img, // A URL
          time: entry.data.time * 1000,
        },
      };
    case 'signUpFreeTrialUpsell':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.data.id,
        data: {
          ...entry.data,
          imgUrl: undefined,
          time: entry.data.time * 1000,
        },
      };
    case 'upsellCrowdsourceFinding':
      return {
        type: entry.type,
        parent_type: entry.parent_type,
        id: entry.data.id,
        data: {
          imgUrl: undefined,
          linkUrl: undefined,
          projectName: entry.data.project_name,
          time: entry.data.time * 1000,
        },
      };
    // These types are old and ignored.
    // TODO: bidHidden is not implemented yet because it's an AB test. See T38861.
    case 'contactRequestSent':
    case 'contactRequestReceived':
    case 'bidHidden':
    case 'sitesBinCreated':
    case 'sitesHandOverMessageSent':
    case 'sitesListingStatusChanged':
    case 'sitesMessageSent':
    case 'sitesOfferCreated':
      return undefined;
    default:
      return assertNever(entry);
  }
}

export function transformProjectData(
  data: WebsocketProjectData,
): NotificationProjectData {
  return {
    ...data,
    appendedDescr: data.appended_descr,
    username: data.userName,
    jobs: data.jobs ? data.jobs.map(Number) : [],
    time: data.time * 1000,
  };
}



// WEBPACK FOOTER //
// /mnt/jenkins-slave-workspace/workspace/gaf-build-webapp-compat/webapp/src/app/@shared/datastore/navigation/notifications/notification.transformers.ts